import {
  SkyBadge,
  SkyButton,
  SkyDataGrid,
  SkyMenu,
  SkyText,
  type SkyDataGridColumn,
  type SkyMenuContent,
} from "@skypointcloud/skypoint-ui";
import { dbtModelCountsOf } from "@shared/types/dbtModelCounts";
import { useMemo, type CSSProperties, type ReactElement } from "react";
import { Link } from "wouter";
import type { TransformationGraph, DbtJobRunRow, DbtJobView } from "@shared/types/transformations";
import { EditIcon, MoreActionsIcon } from "@/lib/icons";
import { absolutePath } from "@/lib/routes";
import { actionsColumnWhen, LIST_GRID_PAGINATION, pixelColumnLayout } from "@/lib/grid";
import {
  attributeToGraph,
  attributeToOwnRun,
  attributedRunWords,
  newerRunWords,
  orderByDependencies,
  jobTestsReading,
  runNowOffReason,
  ownRunWords,
  ranInStep,
  runStartOf,
  stoppedStepWords,
  transformStepPresentation,
} from "./transformFormatting";
import { formatInstant, instantHoverTitle } from "@/lib/time/displayTime";
import { useDisplayTz } from "@/hooks/use-display-tz";

/**
 * The dbt jobs table (canvas 15 block 01; FR-62, FR-13, SEC-17).
 *
 * Rows are in dependency order — the order the schedule editor shows and the
 * runner merges selectors into the bundle. The Last run and Last tests cells
 * are attributed to the row when the graph's run is the last step and the
 * server could evaluate the row's selector (`selection[].known`, `tag:` and
 * `path:` included): the worst status of the models it selected, their rows,
 * and the tests on them, in words (round 6, W7: "Failed", "2 of its models
 * failed"). A row whose selector could not be evaluated repeats the whole run's
 * value and says "Whole run". Never a per-row status the API did not send.
 *
 * Hiding a control is a courtesy, not the control: a Viewer sees the names as
 * links and no Edit, Disable or Delete.
 */

export interface DbtJobsGridProps {
  rows: DbtJobView[];
  graph: TransformationGraph | undefined;
  /** The step the rows describe: the newest, or while one is in flight the newest finished. */
  lastStep: DbtJobRunRow | null;
  /** #841: the newer step still queued or running while `lastStep` is a finished one. */
  inFlight?: DbtJobRunRow | null;
  canWrite: boolean;
  /** `runs.start`: a role with it gets Run now in each job's menu (#1293). */
  canStart?: boolean;
  /** Run now for one job: a run of that job only, the jobs it depends on not included. */
  onRunNow?: (row: DbtJobView) => void;
  /** Why Run now is off for the whole list (project not loaded, unreadable or not bound), or null. */
  runNowBlock?: string | null;
  onEdit: (row: DbtJobView) => void;
  onSetEnabled: (row: DbtJobView, enabled: boolean) => void;
  onDelete: (row: DbtJobView) => void;
}

function OnOff({ on }: { on: boolean }) {
  return (
    <SkyBadge appearance="tint" color={on ? "success" : "subtle"}>
      {on ? "On" : "Off"}
    </SkyBadge>
  );
}

type Attribution = ReturnType<typeof attributeToGraph>;

/** A test count badge over its line: "2 failed" (or "5 passed") and what the count covers. */
function testsBadge(rowId: string, ok: number, failed: number, line: string): ReactElement {
  return (
    <div className="flex flex-col gap-1" data-testid={`last-tests-${rowId}`}>
      <SkyBadge appearance="tint" color={failed > 0 ? "danger" : "success"}>
        {failed > 0 ? `${failed} failed` : `${ok} passed`}
      </SkyBadge>
      <SkyText size={200} color="muted">
        {line}
      </SkyText>
    </div>
  );
}

function muted(text: string, testId?: string): ReactElement {
  return (
    <SkyText size={200} color="muted" testId={testId}>
      {text}
    </SkyText>
  );
}

/**
 * The Last tests cell, from `jobTestsReading` (#1386, the one decision the Overview's tests card
 * reads too): the job's own run's tests when the last step did not name it (walk 2026-10-02, D4);
 * else, in the step, its models' tests when they are its own (`stepTestCountsAreOwn`), "Tests
 * off", "Not run · the run stopped before tests ran", "No tests on its models", or the whole run's
 * count. Called from the cell, so it returns the same elements the cell did.
 */
function lastTestsCell(
  row: DbtJobView,
  lastStep: DbtJobRunRow | null,
  graph: TransformationGraph | undefined,
): ReactElement {
  const tests = jobTestsReading(row, lastStep, graph);
  if (tests.path === "own") {
    // D4: its own run's tests (#1074 review: that run's own results, or why there are none).
    const reading = tests.reading;
    if (reading?.kind === "counts") {
      return testsBadge(
        row.id,
        reading.ok,
        reading.failed,
        `${reading.ok} passed · ${reading.scope}`,
      );
    }
    if (reading) return muted(reading.text, `last-tests-${row.id}`);
    return muted("—");
  }
  const reading = tests.reading;
  switch (reading.kind) {
    case "counts": {
      const { ok, failed, attributed } = reading;
      const only = attributed.models.length === 1 ? attributed.models[0] : undefined;
      const scope = only ? only.name : `its ${attributed.models.length} models`;
      return testsBadge(row.id, ok, failed, `${ok} passed on ${scope}`);
    }
    case "testsOff":
      return muted("Tests off", `last-tests-${row.id}`);
    case "stoppedBeforeTests":
      // #1074 review: the step stopped before dbt's tests: no test ran, for a reason.
      return muted("Not run · the run stopped before tests ran", `last-tests-${row.id}`);
    case "noTests":
      // Known selection, and no test on any of its nodes ran.
      return muted("No tests on its models", `last-tests-${row.id}`);
    case "runLevel":
      return wholeRunTests(row.id, reading.failed);
  }
}

/** The whole run's tests badge: not run, how many failed, or passed. */
function wholeRunBadge(failed: number | null): {
  color: "subtle" | "danger" | "success";
  text: string;
} {
  if (failed === null) return { color: "subtle", text: "Not run" };
  if (failed > 0) return { color: "danger", text: `${failed} failed` };
  return { color: "success", text: "Passed" };
}

function wholeRunTests(rowId: string, failed: number | null): ReactElement {
  const badge = wholeRunBadge(failed);
  return (
    <div className="flex flex-col gap-1" data-testid={`last-tests-${rowId}`}>
      <SkyBadge appearance="tint" color={badge.color}>
        {badge.text}
      </SkyBadge>
      <SkyText size={200} color="muted">
        Whole run
      </SkyText>
    </div>
  );
}

export function DbtJobsGrid({
  rows,
  graph,
  lastStep,
  inFlight = null,
  canWrite,
  canStart = false,
  onRunNow,
  runNowBlock = null,
  onEdit,
  onSetEnabled,
  onDelete,
}: DbtJobsGridProps) {
  const zone = useDisplayTz();
  const ordered = useMemo(() => orderByDependencies(rows), [rows]);
  const namesById = useMemo(() => new Map(rows.map((row) => [row.id, row.name])), [rows]);
  // #841: while a newer step is queued or running, the rows read the last finished step and say
  // a newer one is on its way, never "Queued" over "Not run".
  // #2327: only a job the queued or running step holds says so; a step that names no jobs (an
  // older run) keeps `ranInStep`'s reading.
  const newerFor = (row: DbtJobView) =>
    inFlight && ranInStep(row, inFlight) ? ` · ${newerRunWords(inFlight)}` : "";
  const runLevel = transformStepPresentation(lastStep);
  // Attribution is about the GRAPH's run; a newer step whose graph could not be read
  // reads the whole run's outcome ("Whole run").
  const graphIsLastStep = Boolean(lastStep && graph?.run?.runId === lastStep.runId);
  const attribute = (row: DbtJobView) => (graphIsLastStep ? attributeToGraph(row, graph) : null);

  const columns: SkyDataGridColumn<DbtJobView>[] = [
    {
      key: "name",
      header: "Name",
      width: "19%",
      renderCell: (_value, row) => (
        <Link
          href={absolutePath(`/transformations/${row.id}`)}
          className="underline-offset-2 hover:underline"
        >
          <SkyText size={300} weight="semibold" color="accent">
            {row.name}
          </SkyText>
        </Link>
      ),
    },
    {
      key: "dbtSelector",
      header: "Selector",
      width: "15%",
      renderCell: (_value, row) =>
        row.dbtSelector ? (
          <SkyText size={200} font="monospace" block truncate title={row.dbtSelector}>
            {row.dbtSelector}
          </SkyText>
        ) : (
          <SkyText
            size={200}
            color="muted"
            title="A blank selector is left out of the run, never widened to the whole project."
          >
            (no selector: left out of runs)
          </SkyText>
        ),
    },
    {
      key: "enabled",
      header: "Enabled",
      // #1196: pixel widths hold the whole header (label plus 32 px of padding) at 1024 and 1280.
      width: "106px",
      renderCell: (_value, row) => <OnOff on={row.enabled} />,
    },
    {
      key: "runTests",
      header: "Run tests",
      width: "104px",
      renderCell: (_value, row) => <OnOff on={row.runTests} />,
    },
    {
      key: "dependsOn",
      header: "Depends on",
      width: "13%",
      renderCell: (_value, row) =>
        row.dependsOn.length === 0 ? (
          <SkyText size={200} color="muted">
            —
          </SkyText>
        ) : (
          <SkyText size={200} block>
            {row.dependsOn.map((id) => namesById.get(id) ?? id).join(", ")}
          </SkyText>
        ),
    },
    {
      key: "lastRun",
      header: "Last run",
      width: "16%",
      renderCell: (_value, row) => {
        // A row the step did not name (created or enabled after it) is never
        // shown with that step's outcome. Walk 2026-10-02, D4: one that ran in a run of its own
        // reads that run instead (`attributeToOwnRun`).
        if (!lastStep || !ranInStep(row, lastStep)) {
          // #1074: also a run that failed before dbt reported a node, by its run record.
          const own = attributeToOwnRun(row, graph);
          if (own) {
            const { word, line, color } = ownRunWords(own);
            return (
              <div className="flex flex-col gap-1" data-testid={`last-run-${row.id}`}>
                <SkyBadge appearance="tint" color={color}>
                  {word}
                </SkyBadge>
                <SkyText size={200} color="muted">
                  {line ? `${line} · ` : ""}its own run
                  {own.at
                    ? `, ${own.atIsFinish ? "finished" : "last model built"} ${formatInstant(own.at, zone, "seconds")}`
                    : ""}
                  {/* #1074 (QA 10/16): a job disabled since its own run still reads that run. */}
                  {row.enabled ? "" : " · disabled"}
                  {newerFor(row)}
                </SkyText>
              </div>
            );
          }
          return (
            <SkyText size={200} color="muted">
              Not in the last run
              {newerFor(row)}
            </SkyText>
          );
        }
        const newer = newerFor(row);
        const attributed = attribute(row);
        // #1074 review: a step that stopped before dbt reported any of this job's models.
        const stopped = attributed ? stoppedStepWords(attributed, lastStep) : null;
        if (stopped) {
          return (
            <div className="flex flex-col gap-1" data-testid={`last-run-${row.id}`}>
              <SkyBadge appearance="tint" color={stopped.color}>
                {stopped.word}
              </SkyBadge>
              <SkyText size={200} color="muted">
                {stopped.line}
                {" · started "}
                <span title={instantHoverTitle(runStartOf(lastStep), zone)}>
                  {formatInstant(runStartOf(lastStep), zone, "seconds")}
                </span>
                {newer}
              </SkyText>
            </div>
          );
        }
        if (attributed) {
          const { word, line } = attributedRunWords(attributed);
          return (
            <div className="flex flex-col gap-1" data-testid={`last-run-${row.id}`}>
              <SkyBadge
                appearance="tint"
                color={
                  attributed.models.length === 0
                    ? "subtle"
                    : (attributed.outcome?.color ?? "subtle")
                }
              >
                {word}
              </SkyBadge>
              <SkyText size={200} color="muted">
                {line}
                {inFlight
                  ? ` · started ${formatInstant(runStartOf(lastStep), zone, "seconds")}${newer}`
                  : ""}
              </SkyText>
            </div>
          );
        }
        // Round 6 (W7): the whole run's outcome, said as such ("run-level" was jargon).
        return (
          <div className="flex flex-col gap-1" data-testid={`last-run-${row.id}`}>
            <SkyBadge appearance="tint" color={runLevel.color}>
              {runLevel.label}
            </SkyBadge>
            <SkyText size={200} color="muted">
              Whole run
              {/* Round 6, T5: ok is the models that succeeded, never a skipped one. */}
              {lastStep.modelsReported !== null
                ? ` · ${dbtModelCountsOf(lastStep)?.built ?? 0} models ok`
                : ""}
              {" · started "}
              <span title={instantHoverTitle(runStartOf(lastStep), zone)}>
                {formatInstant(runStartOf(lastStep), zone, "seconds")}
              </span>
              {newer}
            </SkyText>
          </div>
        );
      },
    },
    {
      key: "lastTests",
      header: "Last tests",
      width: "14%",
      renderCell: (_value, row) => lastTestsCell(row, lastStep, graph),
    },
    ...actionsColumnWhen<DbtJobView>(canWrite || (canStart && onRunNow !== undefined), {
      key: "actions",
      header: "Actions",
      width: "6%",
      align: "right",
      renderCell: (_value, row) => {
        // A Viewer gets no controls and no Actions column (FR-13, #1196). Run now needs
        // `runs.start`, the job's own switches need `dbt_jobs.write` (#1293).
        const items: SkyMenuContent[] = [];
        if (canStart && onRunNow) {
          const off = runNowBlock ?? runNowOffReason(row, canWrite);
          items.push({
            key: "run-now",
            label: "Run now",
            description: off ?? undefined,
            disabled: off !== null,
            onClick: () => onRunNow(row),
          });
        }
        if (canWrite) {
          if (items.length > 0) items.push({ kind: "divider", key: "divider-run" });
          items.push(
            {
              key: "toggle",
              label: row.enabled ? "Disable" : "Enable",
              onClick: () => onSetEnabled(row, !row.enabled),
            },
            { kind: "divider", key: "divider" },
            { key: "delete", label: "Delete", danger: true, onClick: () => onDelete(row) },
          );
        }
        return (
          <span className="flex items-center justify-end gap-1">
            {canWrite ? (
              <SkyButton
                appearance="subtle"
                size="small"
                icon={EditIcon}
                aria-label={`Edit ${row.name}`}
                onClick={() => onEdit(row)}
                testId={`transformation-edit-${row.id}`}
              >
                Edit
              </SkyButton>
            ) : null}
            <SkyMenu
              align="end"
              items={items}
              trigger={
                <SkyButton
                  appearance="subtle"
                  size="small"
                  icon={MoreActionsIcon}
                  aria-label={`More actions for ${row.name}`}
                  testId={`transformation-actions-${row.id}`}
                />
              }
            />
          </span>
        );
      },
    }),
  ];

  const layout = pixelColumnLayout(columns, {
    minimums: {
      name: 140,
      dbtSelector: 110,
      dependsOn: 108,
      lastRun: 130,
      lastTests: 116,
      actions: 130,
    },
    selection: false,
  });
  const enabledCount = rows.filter((row) => row.enabled).length;

  return (
    <section aria-label="dbt jobs, in dependency order">
      <div style={{ "--jobs-grid-floor": `${layout.floor}px` } as CSSProperties}>
        <SkyDataGrid<DbtJobView>
          data={ordered}
          columns={layout.columns}
          className="[&_table]:min-w-(--jobs-grid-floor) [&_th]:whitespace-normal"
          {...LIST_GRID_PAGINATION}
          getRowId={(row) => row.id}
          emptyMessage="No dbt jobs yet"
          testId="transformations-grid"
        />
      </div>
      {/* Round 6 (W7): counts and the order, without the runner's words. */}
      <SkyText size={200} color="muted" block className="mt-2" testId="transformations-count">
        {rows.length} dbt job{rows.length === 1 ? "" : "s"} · {enabledCount} enabled · in the order
        they run
      </SkyText>
    </section>
  );
}
